// @flow

import _ from 'lodash';
import React from 'react';
import Checkmark from 'client/components/checkmark';
import InputDate, { type InputDateProps } from 'client/components/form/inputDate';
import InputGroup, { type InputGroupProps } from 'client/components/form/inputGroup';
import InputPhone, { type InputPhoneProps } from 'client/components/form/inputPhone';
import Segment, { type SegmentProps } from 'client/components/form/segment';
import Select, { type SelectProps } from 'client/components/form/select';
import type { FieldProps } from 'redux-form';

import Translate from 'client/components/translate';
import QuestionMark from 'client/components/questionmark';
import { EDE_BACKGROUND_PRIMARY_INFO_PATH } from 'routes/marketplace/constants';

type MedicaidHelpTextType = {
  dob: string | null,
  enteredAge: number,
  isNotSeekingCoverage: boolean,
  firstNameLastName: string | boolean,
  handleclick: Function,
};

const SIXTY_FOUR = 64;

export const renderMedicaidHelpText = ({
  dob,
  enteredAge,
  isNotSeekingCoverage,
  firstNameLastName,
  handleclick,
}: MedicaidHelpTextType) => {
  // only render help text for the member who is seeking coverage
  if (isNotSeekingCoverage) return null;

  const birthYear = dob ? dob.split('/')[2] : '';
  const currentYear = new Date().getFullYear();
  // Only update when user enter 4 digit year in the form
  const age = birthYear && birthYear.length > 4 ? currentYear - Number(birthYear) : enteredAge;

  return age >= SIXTY_FOUR ? (
    <div className="question flex-row sub">
      <Translate
        className="blue-0-color"
        path={`${EDE_BACKGROUND_PRIMARY_INFO_PATH}.dob.helpText.${
          age > SIXTY_FOUR ? 'medEligible' : 'almostMedEligible'
        }`}
        using={{ firstNameLastName }} />
      <QuestionMark className="margin-left" checked={false} clickHandler={handleclick} />
    </div>
  ) : null;
};

export const renderAcknowledgementCheckmark = ({ input, meta: { touched, error } }: FieldProps) => {
  const checked = !!input.value;

  return (
    <div className="pad-y-2">
      {touched && error && (
        <div className="error-message">
          <Translate path="ede.error.acknowledgment" />
        </div>
      )}
      <div className="flex-row center left">
        <Checkmark
          id={input.name}
          className="grey-4-color"
          error={touched && error}
          checked={checked}
          clickHandler={(e) => {
            input.onChange(!checked);
            e.preventDefault();
          }} />
        <div className="pad-left">
          <p>
            <Translate path="ede.common.acknowledgementAgreement" />
          </p>
        </div>
      </div>
    </div>
  );
};

export const renderDateInput = (
  { input, meta: { error, touched }, meta }: FieldProps,
  { changeHandler, ...props }: InputDateProps,
) => (
  <InputDate
    {...input}
    id={input.name}
    error={touched && error}
    changeHandler={(e: SyntheticEvent<>) => {
      if (changeHandler) {
        changeHandler(input, meta, e);
      }
      input.onChange(e);
    }}
    {...props} />
);

export const renderTextInput = (
  { input, meta: { error, touched }, meta }: FieldProps,
  { changeHandler, ...props }: InputGroupProps & { changeHandler: Function },
) => (
  <InputGroup
    {...input}
    id={input.name}
    error={touched && error}
    onChange={(e: SyntheticEvent<>) => {
      if (changeHandler) {
        changeHandler(input, meta, e);
      }
      input.onChange(e);
    }}
    {...props} />
);

export const renderPhoneInput = (
  { input, meta: { error, touched }, meta }: FieldProps,
  { changeHandler, ...props }: InputPhoneProps,
) => (
  <InputPhone
    {...input}
    id={input.name}
    error={touched && error}
    changeHandler={(val) => {
      if (changeHandler) {
        changeHandler(input, meta);
      }
      input.onChange(val);
    }}
    {...props} />
);

export const renderSelector = (
  { input, meta: { error, touched }, meta }: FieldProps,
  { changeHandler, options, ...props }: SelectProps,
) => (
  <Select
    {...input}
    id={input.name}
    error={touched && error}
    changeHandler={(val) => {
      if (changeHandler) {
        return changeHandler(input, meta, val);
      }
      return input.onChange(val);
    }}
    options={options}
    {...props} />
);

export const renderSegment = (
  { input, meta: { error, touched }, meta }: FieldProps,
  { clickHandler, options, ...props }: SegmentProps,
) => (
  <Segment
    {...input}
    id={input.name}
    error={touched && error}
    clickHandler={(val) => {
      if (clickHandler) {
        clickHandler(input, meta);
      }
      input.onChange(val);
    }}
    options={options}
    {...props} />
);

export const renderBooleanCheckmark = ({
  input,
  clickHandler,
}: {
  ...$Exact<FieldProps>,
  clickHandler: Function,
}) => (
  <Checkmark
    id={input.name}
    checked={input.value}
    clickHandler={(e) => {
      e.preventDefault();
      input.onChange(!input.value);
      if (_.isFunction(clickHandler)) {
        clickHandler(input.name, !input.value);
      }
    }} />
);
